import * as React from 'react';
import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@klareospace/pharos-lib-front-utils';
import type { VehicleStatus } from '@klareospace/pharos-lib-types';
import { VEHICLE_STATUS } from '@klareospace/pharos-lib-types';
import {
  ActionBar,
  ActionBarDivider,
  ActionBarSpacer,
  Button,
  InfoPopover,
  MultiSelect,
  SearchInput,
} from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CarrierOption {
  label: string;
  value: string;
}

export interface TemplateOption {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}

export interface VehiclesActionBarProps {
  carrierFilter?: string[];
  carriers?: CarrierOption[];
  onAdd: () => void;
  onCarrierFilterChange?: (values: string[]) => void;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (values: string[]) => void;
  onTemplateFilterChange?: (values: string[]) => void;
  searchValue: string;
  statusFilter: VehicleStatus[];
  templateFilter?: string[];
  templates?: TemplateOption[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VEHICLE_STATUSES: VehicleStatus[] = [VEHICLE_STATUS.AVAILABLE, VEHICLE_STATUS.IN_MAINTENANCE];

// ─── Component ────────────────────────────────────────────────────────────────

export function VehiclesActionBar({
  carrierFilter,
  carriers,
  onAdd,
  onCarrierFilterChange,
  onSearchChange,
  onStatusFilterChange,
  onTemplateFilterChange,
  searchValue,
  statusFilter,
  templateFilter,
  templates,
}: VehiclesActionBarProps): React.JSX.Element {
  const { t } = useTranslation('settings');

  const statusOptions = useMemo(
    () =>
      VEHICLE_STATUSES.map((status) => ({
        label: t(`vehicles.statuses.${status}`),
        value: status,
      })),
    [t],
  );

  const multiSelectCommonProps = useMemo(
    () => ({
      hideClear: true,
      hideClose: true,
      maxCount: 2,
      showLabelWhenFiltering: true,
    }),
    [],
  );

  return (
    <ActionBar>
      <SearchInput
        ariaLabel={t('vehicles.actionBar.search.ariaLabel')}
        className="w-[200px]"
        onChange={onSearchChange}
        placeholder={t('vehicles.actionBar.search.placeholder')}
        value={searchValue}
      />
      <ActionBarDivider />
      {carriers !== undefined && carriers.length > 0 && onCarrierFilterChange !== undefined && (
        <MultiSelect
          {...multiSelectCommonProps}
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={carrierFilter}
          label={t('vehicles.actionBar.filters.carrier')}
          onValueChange={onCarrierFilterChange}
          options={carriers}
          placeholder={t('vehicles.actionBar.filters.allCarriers')}
          searchable={false}
          variant="compact"
        />
      )}
      {templates !== undefined && templates.length > 0 && onTemplateFilterChange !== undefined && (
        <MultiSelect
          {...multiSelectCommonProps}
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={templateFilter}
          label={t('vehicles.actionBar.filters.vehicleTemplate')}
          onValueChange={onTemplateFilterChange}
          options={templates}
          placeholder={t('vehicles.actionBar.filters.allTemplates')}
          searchable={false}
          variant="compact"
        />
      )}
      <MultiSelect
        {...multiSelectCommonProps}
        className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
        defaultValue={statusFilter}
        label={t('vehicles.actionBar.filters.availability')}
        onValueChange={onStatusFilterChange}
        options={statusOptions}
        placeholder={t('vehicles.actionBar.filters.allStatuses')}
        searchable={false}
        variant="compact"
      />
      <ActionBarSpacer />
      <InfoPopover description={t('vehicles.actionBar.tooltip.body')} title={t('vehicles.actionBar.tooltip.title')} />
      <RequirePermission permission="vehicles:manage">
        <Button
          className="h-9 gap-1.5 rounded-md px-3 text-sm shadow-sm hover:shadow-md"
          onClick={onAdd}
          variant="default"
        >
          <Plus className="h-4 w-4" />
          {t('vehicles.actionBar.addVehicle')}
        </Button>
      </RequirePermission>
    </ActionBar>
  );
}
